/** Phase E — one payer-scoped Payout Run: Gross Entitlement · Downline Obligations · Retained Economics. */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import useAuthStore from '../../lib/auth-store';
import { StatusPill, btnGhost, btnPrimary, btnSmall, errorText, inputCls, money } from '../finance/financeUi';

const Stat = ({ label, value, hint, testid }) => (
  <div className="rounded-lg border px-4 py-3">
    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-1 text-2xl font-semibold tabular-nums" data-testid={testid}>{value}</p>
    {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
  </div>
);

export const RunNumbers = ({ run }) => (
  <div className="grid gap-3 sm:grid-cols-3" data-testid="run-numbers">
    <Stat label="Gross entitlement" value={money(run.gross_entitlement)} testid="run-gross"
          hint={run.payer_kind === 'REVENUE_OWNER' ? 'Native economic margin' : run.incoming_from_name ? `From ${run.incoming_from_name}` : null} />
    <Stat label={run.payer_kind === 'REVENUE_OWNER' ? 'Direct obligations' : 'Downline obligations'} value={money(run.downline_obligations)} testid="run-downline" hint={`${run.line_count} payee(s) this payer owes`} />
    <Stat label="Retained economics" value={money(run.retained_economics)} testid="run-retained" />
  </div>
);

function SettleForm({ line, onDone }) {
  const [f, setF] = useState({ method: 'EXTERNAL_ACH', reference: '', settled_on: new Date().toISOString().slice(0, 10) });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try { onDone(await api.request(`/payer-runs/lines/${line.line_id}/settle`, { method: 'POST', body: JSON.stringify(f) })); toast.success('Settlement recorded'); } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`settle-form-${line.line_id}`}>
      <select className={`${inputCls} w-36`} value={f.method} onChange={set('method')} data-testid="settle-method">
        {['EXTERNAL_ACH', 'WIRE', 'CHECK', 'OTHER'].map((m) => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
      </select>
      <input className={`${inputCls} w-40`} placeholder="Bank reference" value={f.reference} onChange={set('reference')} data-testid="settle-reference" />
      <input type="date" className={`${inputCls} w-40`} value={f.settled_on} onChange={set('settled_on')} data-testid="settle-date" />
      <button type="button" className={btnSmall} disabled={!f.reference.trim()} onClick={save} data-testid="settle-submit-btn">Record</button>
    </div>
  );
}

function Lines({ run, onChange }) {
  const [open, setOpen] = useState(null);
  const canSettle = ['READY_FOR_SETTLEMENT', 'EXTERNAL_SETTLEMENT_REQUIRED'].includes(run.status);
  return (
    <ul className="divide-y rounded-lg border text-sm" data-testid="run-lines">
      {run.lines.map((ln) => (
        <li key={ln.line_id} className="space-y-2 px-4 py-2.5" data-testid={`run-line-${ln.line_id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-medium">{ln.payee_name} <span className="text-xs font-normal text-muted-foreground">· {ln.payee_kind}</span></span>
            <span className="flex items-center gap-3">
              {typeof ln.downstream_funding === 'number' && ln.downstream_funding > 0 && <span className="text-xs text-muted-foreground">own {money(ln.own_amount)} + funds downline {money(ln.downstream_funding)}</span>}
              <span className="tabular-nums font-semibold" data-testid={`line-amount-${ln.line_id}`}>{money(ln.amount)}</span>
              <StatusPill value={ln.status} testid={`line-status-${ln.line_id}`} />
              {canSettle && ln.status === 'APPROVED' && <button type="button" className={btnSmall} onClick={() => setOpen(open === ln.line_id ? null : ln.line_id)} data-testid={`settle-toggle-${ln.line_id}`}>Record settlement</button>}
            </span>
          </div>
          {ln.settlement && <p className="text-xs text-muted-foreground" data-testid={`line-settlement-${ln.line_id}`}>{ln.settlement.method} · {ln.settlement.reference} · {ln.settlement.settled_on} · recorded externally</p>}
          {open === ln.line_id && <SettleForm line={ln} onDone={(r) => { setOpen(null); onChange(r); }} />}
        </li>
      ))}
    </ul>
  );
}

const NEXT = { DRAFT: [['UNDER_REVIEW', 'Submit for review']], UNDER_REVIEW: [['APPROVED', 'Approve'], ['DRAFT', 'Return to draft']] };

function Actions({ run, isMsc, onChange }) {
  const [note, setNote] = useState('');
  const intervention = isMsc && run.payer_user_id;
  const move = async (to) => {
    try { onChange(await api.request(`/payer-runs/runs/${run.run_id}/transition`, { method: 'POST', body: JSON.stringify({ to_status: to, note: note || null }) })); setNote(''); toast.success(`Run ${to.replace(/_/g, ' ').toLowerCase()}`); } catch (e) { toast.error(errorText(e)); }
  };
  const steps = [...(NEXT[run.status] || []), ...(isMsc && NEXT[run.status] ? [['CANCELLED', 'Cancel']] : [])];
  if (!steps.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="run-actions">
      {intervention && <input className={`${inputCls} max-w-xs`} placeholder="MSC intervention note (required)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="run-intervention-note" />}
      {steps.map(([to, label]) => (
        <button key={to} type="button" className={to === 'APPROVED' ? btnPrimary : btnGhost} disabled={intervention && !note.trim()} onClick={() => move(to)} data-testid={`run-action-${to.toLowerCase()}`}>{label}</button>
      ))}
    </div>
  );
}

function SettlementPath({ run }) {
  const me = useAuthStore((st) => st.user);
  const mine = me && run.payer_user_id && (me.id === run.payer_user_id || me.user_id === run.payer_user_id);
  if (run.settlement_path === 'PAYOUT_RAIL') {
    return <p className="text-xs text-muted-foreground" data-testid="run-settlement-path">Payment Account: <b className="text-emerald-700">READY</b> · Automated ACH: not enabled yet — record settlement here</p>;
  }
  return (
    <p className="text-xs text-muted-foreground" data-testid="run-settlement-path">Settlement path: external settlement required — record it here.
      {mine && <> To send payouts directly through ISOCNTRL later, <Link to="/my-settings?tab=banking" className="underline" data-testid="run-setup-payments-link">set up your Payment Account</Link>.</>}
    </p>
  );
}

export function RunDetail({ run, isMsc, onChange }) {
  return (
    <div className="space-y-4 p-5" data-testid={`run-detail-${run.run_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-lg font-medium" data-testid="run-payer-name">{run.payer_name} <span className="text-sm font-normal text-muted-foreground">· {run.period} · {run.payer_kind.replace('_', ' ')}</span></p>
          <SettlementPath run={run} />
        </div>
        <StatusPill value={run.status} testid="run-status" />
      </div>
      {run.synthetic && <p className="rounded-md border border-dashed border-amber-500 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900" data-testid="run-synthetic-banner">Synthetic demo data — no real payments</p>}
      {run.post_lock_drift && <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="run-drift">Approved amounts are locked. A later recalculation differs ({money(run.post_lock_drift.recomputed_downline_obligations)}) — handle it as an adjustment next period.</p>}
      <RunNumbers run={run} />
      <Actions run={run} isMsc={isMsc} onChange={onChange} />
      <Lines run={run} onChange={onChange} />
    </div>
  );
}
